import { useState } from 'react';
import { useAuth } from '../auth/context.js';
import { ApiError } from '../api/client.js';
import { labels } from '../labels.js';
import { Icon } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import { VITRINE_URL } from '../lib/vitrine.js';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'INVALID_CREDENTIALS') setError(labels.auth.invalidCredentials);
        else if (e.code === 'ACCOUNT_DISABLED') setError(labels.auth.accountDisabled);
        else if (e.code === 'RATE_LIMITED') setError(labels.auth.rateLimited);
        else setError(labels.errors.generic);
      } else {
        setError(labels.errors.network);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={styles.screen}>
      <section className={styles.brand} aria-hidden="true">
        <Logo size={40} withWordmark tone="dark" />
        <div className={styles.brandBody}>
          <h2 className={styles.brandTitle}>{labels.auth.brandTitle}</h2>
          <p className={styles.brandText}>{labels.auth.brandText}</p>
          <ul className={styles.points}>
            {labels.auth.brandPoints.map((point) => (
              <li key={point}>
                <span className={styles.pointIcon}><Icon name="check" size={16} /></span>
                {point}
              </li>
            ))}
          </ul>
        </div>
        <p className={styles.brandFoot}>{labels.app.name}</p>
      </section>

      <section className={styles.formSide}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <span className={styles.mobileLogo}><Logo size={40} withWordmark /></span>
          <div>
            <h1 className="text-title-1" style={{ margin: 0 }}>{labels.auth.title}</h1>
            <p className="page-subtitle">{labels.auth.intro}</p>
          </div>

          {error && (
            <div role="alert" className="notice notice--danger" style={{ margin: 0 }}>
              <Icon name="alert" size={18} />
              {error}
            </div>
          )}

          <div className="field">
            <label htmlFor="email" className="field-label">{labels.auth.email}</label>
            <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" autoComplete="username" autoFocus />
          </div>

          <div className="field">
            <label htmlFor="password" className="field-label">{labels.auth.password}</label>
            <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input" autoComplete="current-password" />
          </div>

          <button type="submit" disabled={loading} className="btn-primary btn-block btn-lg">
            {loading ? labels.auth.loggingIn : labels.auth.submit}
            {!loading && <Icon name="arrow-right" size={18} />}
          </button>

          {VITRINE_URL && (
            <a href={VITRINE_URL} className="btn-link" style={{ justifySelf: 'center' }}>
              <Icon name="arrow-left" size={16} />
              {labels.auth.backToVitrine}
            </a>
          )}
        </form>
      </section>
    </main>
  );
}
